// store/slices/agentSlice.ts
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface TokenStatus {
  total: number;
  used: number;
  remaining: number;
  available: boolean;
  expiresAt: string;
}

export interface SessionData {
  /** SDP answer from OpenAI, relayed by our backend. */
  sdp: string;
  /** OpenAI call id (from the Location header), for support/observability. */
  callId: string | null;
  userTokens: TokenStatus;
}

interface AgentState {
  isConnected: boolean;
  isConnecting: boolean;
  isMuted: boolean;
  tokenStatus: TokenStatus | null;
  error: string | null;
  sessionData: SessionData | null;
}

const initialState: AgentState = {
  isConnected: false,
  isConnecting: false,
  isMuted: false,
  tokenStatus: null,
  error: null,
  sessionData: null,
};

const agentSlice = createSlice({
  name: "agent",
  initialState,
  reducers: {
    setConnecting: (state, action: PayloadAction<boolean>) => {
      state.isConnecting = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      state.isConnecting = false;
      if (!action.payload) {
        state.isMuted = false;
        state.sessionData = null;
      }
    },
    setMuted: (state, action: PayloadAction<boolean>) => {
      state.isMuted = action.payload;
    },
    setTokenStatus: (state, action: PayloadAction<TokenStatus>) => {
      state.tokenStatus = action.payload;
    },
    updateTokenUsage: (state, action: PayloadAction<number>) => {
      if (state.tokenStatus) {
        const tokensUsed = action.payload;
        const newUsed = state.tokenStatus.used + tokensUsed;
        const newRemaining = state.tokenStatus.total - newUsed;

        state.tokenStatus = {
          ...state.tokenStatus,
          used: newUsed,
          remaining: newRemaining,
          available: newRemaining > 0,
        };
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setSessionData: (state, action: PayloadAction<SessionData | null> ) => {
      state.sessionData = action.payload;
      if (action?.payload) {
        state.tokenStatus = action.payload.userTokens;
      }
    },
    resetAgent: () => initialState,
  },
});

export const {
  setConnecting,
  setConnected,
  setMuted,
  setTokenStatus,
  updateTokenUsage,
  setError,
  setSessionData,
  resetAgent,
} = agentSlice.actions;

export default agentSlice.reducer;
